import { ContentfulLivePreview } from '@contentful/live-preview';
import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Locale } from '../../translations/translation';
import { getLocaleFromPath } from '../../utils/get-locale-from-path';

  if (typeof window !== 'undefined') {
    ContentfulLivePreview.init({
      locale: 'en-US',
      enableInspectorMode: true,
      enableLiveUpdates: true,
      debugMode: true,
    });
  }

  type LocationLike = {
    pathname: string;
  };

  export type WrapPageWithContentfulLivePreviewProps = {
    element: ReactNode;
    location: LocationLike;
  };

  function shouldEnableContentfulLivePreview(): boolean {
    if (process.env.GATSBY_CONTENTFUL_LIVE_PREVIEW === 'true') {
      return true;
    }
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  function siteLocaleToContentfulLocale(siteLocale: Locale): string {
    if (siteLocale === Locale.Japanese) {
      return process.env.GATSBY_CONTENTFUL_PREVIEW_LOCALE_JP || 'ja';
    }
    return siteLocale as string;
  }

  export function WrapPageWithContentfulLivePreview({
    element,
    location,
  }: WrapPageWithContentfulLivePreviewProps): ReactElement {

  // Match SSR: only the env flag is known on the server. Iframe preview without
  // the env var is handled after mount (see effect) and early `init()` in gatsby-browser.js.
  const [livePreviewActive, setLivePreviewActive] = useState(
    () => process.env.GATSBY_CONTENTFUL_LIVE_PREVIEW === 'true',
  );
  const pathname = location?.pathname || '/';
  const locale = siteLocaleToContentfulLocale(getLocaleFromPath(pathname));
  const targetOrigin = 'https://app.contentful.com';

  useEffect(() => {
    if (livePreviewActive) {
      return;
    }
    if (shouldEnableContentfulLivePreview()) {
      setLivePreviewActive(true);
    }
  }, [livePreviewActive]);

  if (!livePreviewActive) {
    return <>{element}</>;
  }

  return (
    <ContentfulLivePreviewProvider
      locale={locale}
      space={process.env.GATSBY_CONTENTFUL_APP_ID}
      environment={process.env.GATSBY_CONTENTFUL_ENV || 'master'}
      targetOrigin={targetOrigin}
      enableInspectorMode
      enableLiveUpdates
      debugMode
    >
      {element}
    </ContentfulLivePreviewProvider>
  );
}
